import React, { useEffect, useState } from "react";
import { PageHeader } from "./DashboardLayout";
import { api, getErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
    Loader2, Plus, Trash2, Copy, Check, Key, Globe,
    Download, CheckCircle2, ChevronRight, ExternalLink,
    Plug2, Wordpress
} from "lucide-react";

const PLUGIN_ZIP_URL = "/audo-tts.zip";

const WP_STEPS = [
    {
        n: "01",
        title: "Download the Plugin",
        desc: "Click the button above to download audo-tts.zip to your computer.",
        note: null,
    },
    {
        n: "02",
        title: "Upload to WordPress",
        desc: "In your WordPress Admin, go to Plugins → Add New → Upload Plugin. Choose the downloaded zip file and click Install Now.",
        note: "WordPress Admin → Plugins → Add New → Upload Plugin",
    },
    {
        n: "03",
        title: "Activate the Plugin",
        desc: "After installation, click Activate Plugin. You'll see a new Audo TTS menu appear in your WordPress Admin sidebar.",
        note: null,
    },
    {
        n: "04",
        title: "Create an API Key",
        desc: 'Below on this page, create a new API key. Set the key name (e.g. "My WordPress Site") and enter your WordPress site domain. Click Create key and copy the secret — you only see it once.',
        note: "Important: Copy the key immediately after creating it",
    },
    {
        n: "05",
        title: "Paste the API Key in WordPress",
        desc: "In WP Admin → Audo TTS → Settings, paste your API key. Select your preferred voice and tone. Click Save Settings.",
        note: "WP Admin → Audo TTS → Settings",
    },
    {
        n: "06",
        title: "Start Generating Audio",
        desc: "Open any post. In the editor you'll see an Audo TTS meta box on the right. Click Generate Audio. The player appears automatically at the top of the post — no theme changes needed.",
        note: "Audio generates in the background (~5–15 seconds per post)",
    },
];

const TIPS = [
    { t: "Auto-generation", d: "New posts auto-generate audio on publish. No manual action needed." },
    { t: "All themes supported", d: "The player injects above post content. Works with any WordPress theme." },
    { t: "Analytics", d: "Play counts and completion rates are tracked in your Audo dashboard under Analytics." },
    { t: "Regenerate anytime", d: "Changed your article? Hit Regenerate Audio in the post editor meta box." },
    { t: "Team access", d: "Multiple team members can use the same API key, or create separate keys per site." },
    { t: "Voice & tone", d: "Change voice/tone per post or set a global default in plugin settings." },
];

export default function ApiPage() {
    const [keys, setKeys] = useState([]);
    const [keyName, setKeyName] = useState("");
    const [keyDomain, setKeyDomain] = useState("");
    const [creating, setCreating] = useState(false);
    const [newSecret, setNewSecret] = useState(null);
    const [copied, setCopied] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/settings/api-keys");
            setKeys(data);
        } catch (e) {
            toast.error(getErrorMessage(e));
        }
    };

    useEffect(() => {
        load();
    }, []);

    const create = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const { data } = await api.post("/settings/api-keys", {
                name: keyName,
                domain: keyDomain.trim() || undefined,
                key_type: keyDomain.trim() ? "wordpress" : "manual",
            });
            setNewSecret(data);
            setKeyName("");
            setKeyDomain("");
            await load();
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setCreating(false);
        }
    };

    const onDelete = async (id) => {
        if (!confirm("Revoke this API key? It will stop working immediately.")) return;
        try {
            await api.delete(`/settings/api-keys/${id}`);
            await load();
            toast.success("Key revoked");
        } catch (e) {
            toast.error(getErrorMessage(e));
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(newSecret.secret);
        } catch {}
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div>
            <PageHeader eyebrow="API &amp; Plugins" title="WordPress plugin and API keys." subtitle="Download the Audo WordPress plugin, follow the setup guide, and manage your API keys." />

            {/* ── WordPress Plugin Card ────────────────────────────────────────── */}
            <div className="mb-6 bg-white rounded-3xl border border-cream-300 shadow-soft overflow-hidden">
                <div className="flex flex-col lg:flex-row items-stretch">
                    {/* Left accent */}
                    <div className="lg:w-[320px] shrink-0 bg-ink p-8 flex flex-col justify-between">
                        <div>
                            <div className="w-14 h-14 rounded-2xl bg-coral/20 flex items-center justify-center mb-5">
                                <Plug2 size={26} className="text-coral" />
                            </div>
                            <h2 className="font-display text-[28px] font-semibold tracking-tight text-cream-100 leading-tight mb-3">
                                Audo WordPress Plugin
                            </h2>
                            <p className="text-[13px] text-cream-100/60 leading-relaxed">
                                Auto-converts every post to Bengali audio. Works with all themes since WP 5.0.
                            </p>
                        </div>
                        <div className="mt-8">
                            <a
                                href={PLUGIN_ZIP_URL}
                                download="audo-tts.zip"
                                className="w-full inline-flex items-center justify-center gap-2 bg-coral hover:bg-coral/80 text-white rounded-2xl px-5 py-3.5 text-[14px] font-semibold transition-colors"
                            >
                                <Download size={16} />
                                Download Plugin (.zip)
                            </a>
                            <p className="mt-2.5 text-[11px] text-cream-100/40 text-center">v1.0 · ~256KB · WordPress 5.0+</p>
                        </div>
                    </div>

                    {/* Right: steps */}
                    <div className="flex-1 p-8">
                        <h3 className="text-[11px] uppercase tracking-[0.2em] font-semibold text-ink-muted mb-6">Step-by-step setup guide</h3>
                        <div className="space-y-5">
                            {WP_STEPS.map((step, i) => (
                                <div key={i} className="flex gap-4">
                                    <div className="shrink-0 w-9 h-9 rounded-xl bg-coral/10 text-coral flex items-center justify-center font-display font-bold text-[13px]">
                                        {step.n}
                                    </div>
                                    <div className="flex-1 pt-1.5">
                                        <p className="font-semibold text-[14px] text-ink mb-0.5">{step.title}</p>
                                        <p className="text-[13px] text-ink-muted leading-relaxed">{step.desc}</p>
                                        {step.note && (
                                            <p className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] bg-cream-100 text-ink-muted px-2.5 py-1 rounded-lg font-mono">
                                                <ChevronRight size={10} className="text-coral" />
                                                {step.note}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Tips ─────────────────────────────────────────────────────────── */}
            <div className="mb-6 bg-cream-100 rounded-3xl border border-cream-300 p-6">
                <h3 className="text-[11px] uppercase tracking-[0.2em] font-semibold text-ink-muted mb-4">Tips &amp; notes</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {TIPS.map((tip, i) => (
                        <div key={i} className="flex gap-3 bg-white rounded-2xl p-4 border border-cream-300">
                            <CheckCircle2 size={16} className="text-coral shrink-0 mt-0.5" />
                            <div>
                                <p className="text-[13px] font-semibold text-ink">{tip.t}</p>
                                <p className="text-[12px] text-ink-muted mt-0.5">{tip.d}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── API Keys ─────────────────────────────────────────────────────── */}
            <div className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft">
                <div className="flex items-start justify-between mb-1">
                    <h3 className="font-display text-[22px] font-semibold tracking-tight">API Keys</h3>
                </div>
                <p className="text-[13px] text-ink-muted mb-5">
                    Used by the WordPress plugin and direct API integrations. Add a domain to lock the key to a specific site.
                </p>

                {/* New key banner */}
                {newSecret && (
                    <div className="mb-5 bg-sunshine/30 border border-sunshine rounded-3xl p-5">
                        <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink mb-2">New API key — copy it now</p>
                        <p className="text-[12px] text-ink-muted mb-3">This is the only time you will see the full secret. Store it safely.</p>
                        <div className="flex items-center gap-2 bg-ink text-cream-100 rounded-2xl p-3 font-mono text-[13px]">
                            <span className="flex-1 truncate">{newSecret.secret}</span>
                            <button onClick={copy} className="bg-cream-100 text-ink px-3 py-1 rounded-full text-[11px] font-bold inline-flex items-center gap-1">
                                {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
                            </button>
                        </div>
                        <button onClick={() => setNewSecret(null)} className="mt-3 text-[12px] text-ink-muted hover:text-ink underline">Dismiss</button>
                    </div>
                )}

                <form onSubmit={create} className="space-y-3 mb-6">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <input
                            required
                            value={keyName}
                            onChange={(e) => setKeyName(e.target.value)}
                            placeholder="Key name (e.g. My WordPress site)"
                            className="flex-1 bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-coral/40"
                        />
                        <input
                            value={keyDomain}
                            onChange={(e) => setKeyDomain(e.target.value)}
                            placeholder="Domain (e.g. mysite.com) — optional"
                            className="flex-1 bg-cream-100 border border-cream-300 rounded-2xl px-4 py-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-coral/40"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={creating}
                        className="bg-ink hover:bg-coral text-cream-100 rounded-full px-5 py-3 text-[14px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70 transition-colors"
                    >
                        {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                        Create key
                    </button>
                </form>

                {keys.length === 0 ? (
                    <div className="text-center py-10 bg-cream-100 rounded-2xl border border-dashed border-cream-300">
                        <Key className="mx-auto mb-2 text-ink-muted" size={22} />
                        <p className="text-[14px] text-ink-muted">No API keys yet. Create one above to get started.</p>
                    </div>
                ) : (
                    <ul className="divide-y divide-cream-300">
                        {keys.map((k) => (
                            <li key={k.id} className="flex items-center gap-4 py-4">
                                <div className="w-10 h-10 rounded-xl bg-coral/15 text-coral flex items-center justify-center">
                                    {k.key_type === "wordpress" ? <Globe size={15} /> : <Key size={15} />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="font-semibold text-[14px]">{k.name}</p>
                                        {k.key_type === "wordpress" && (
                                            <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">WordPress</span>
                                        )}
                                    </div>
                                    <p className="text-[12px] font-mono text-ink-muted">{k.prefix}••••••••</p>
                                    {k.domain && <p className="text-[11px] text-ink-muted mt-0.5">🌐 {k.domain}</p>}
                                    {k.last_used_at && <p className="text-[11px] text-ink-muted">Last used {new Date(k.last_used_at).toLocaleDateString()}</p>}
                                </div>
                                <button onClick={() => onDelete(k.id)} className="p-2 rounded-lg hover:bg-coral/10 text-ink-muted hover:text-coral transition-colors" aria-label="Revoke">
                                    <Trash2 size={14} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
